import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Lock, ShieldCheck, LogOut } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "cedula-admin-token";

export const getAdminToken = () => {
    try {
        return localStorage.getItem(STORAGE_KEY) || "";
    } catch {
        return "";
    }
};

const setAdminToken = (token) => {
    try {
        if (token) localStorage.setItem(STORAGE_KEY, token);
        else localStorage.removeItem(STORAGE_KEY);
    } catch {
        /* ignore */
    }
};

export const AdminLogoutButton = ({ onLogout }) => (
    <button
        onClick={() => {
            setAdminToken("");
            onLogout?.();
        }}
        data-testid="admin-logout-btn"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#0B192C]/30 hover:border-[#0B192C] label-overline text-xs"
        title="Sign out of admin"
    >
        <LogOut className="w-3 h-3" strokeWidth={1.5}/> Sign out
    </button>
);

const AdminGate = ({ children }) => {
    const [authed, setAuthed] = useState(false);
    const [checking, setChecking] = useState(true);
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // On mount: verify stored token by re-calling login.
    useEffect(() => {
        const stored = getAdminToken();
        if (!stored) {
            setChecking(false);
            return;
        }
        axios.post(`${API}/admin/login`, { password: stored })
            .then(() => setAuthed(true))
            .catch(() => setAdminToken(""))
            .finally(() => setChecking(false));
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await axios.post(`${API}/admin/login`, { password });
            setAdminToken(password);
            setAuthed(true);
            toast.success("Signed in");
        } catch (err) {
            toast.error(err.response?.status === 401 ? "Invalid password" : "Sign-in failed");
            setAdminToken("");
        } finally {
            setSubmitting(false);
        }
    };

    if (checking) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <span className="label-overline text-[#718096]">Checking session…</span>
            </div>
        );
    }

    if (authed) {
        return (
            <>
                {React.Children.map(children, (child) =>
                    React.cloneElement(child, { onLogout: () => setAuthed(false) })
                )}
            </>
        );
    }

    return (
        <div className="min-h-[70vh] flex items-center justify-center px-6">
            <div className="w-full max-w-md border border-[#0B192C]/10 bg-white p-8" data-testid="admin-gate">
                <div className="label-overline text-[#1E3A2B] mb-3 flex items-center gap-2">
                    <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5}/> Restricted
                </div>
                <h1 className="font-serif-display text-4xl tracking-tight mb-2">Admin Sign-In</h1>
                <p className="text-sm text-[#4A5568] mb-6">This panel manages products, leads, sponsorships, and provider portals. Enter the admin password to continue.</p>

                <form onSubmit={onSubmit} className="space-y-4">
                    <div>
                        <Label className="label-overline">Password</Label>
                        <div className="relative mt-1">
                            <Lock className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#718096]" strokeWidth={1.5}/>
                            <Input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••••••"
                                required
                                autoFocus
                                data-testid="admin-password-input"
                                className="pl-9 rounded-none border-[#0B192C]/20 font-mono-body bg-white"
                            />
                        </div>
                    </div>
                    <button
                        type="submit"
                        disabled={submitting || !password}
                        data-testid="admin-signin-btn"
                        className="btn-accent w-full px-6 py-3 label-overline disabled:opacity-50"
                    >
                        {submitting ? "Signing in…" : "Sign In"}
                    </button>
                </form>

                <div className="mt-6 pt-6 border-t border-[#0B192C]/10 text-xs text-[#718096] leading-relaxed">
                    Forgot the password? Update <code className="font-mono-body text-[#0B192C]">ADMIN_PASSWORD</code> in your backend <code className="font-mono-body text-[#0B192C]">.env</code> and redeploy.
                </div>
            </div>
        </div>
    );
};

export default AdminGate;
